import {
  Component,
  h,
  Element,
  Prop,
  State,
  Event,
  EventEmitter,
  Watch,
  Listen,
  forceUpdate,
  Method,
  Fragment,
} from "@stencil/core"
import isNil from "lodash-es/isNil"
import type { Options as FlatpickrOptions } from "flatpickr/dist/types/options"
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance"
import {
  dispatchEvent,
  getNumericValue,
  parseFunction,
  getAppearance,
  animation,
  translate,
  hasSlot,
  renderHiddenInput,
  getNumberOfLines,
} from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-input",
  styleUrl: "input.scss",
  shadow: true,
})
export class Input {
  private animationDuration = 150
  private invokeHitEnterCallback: Function = () => null
  private invokeChangeCallback: Function = () => null
  private invokeInputCallback: Function = () => null
  private invokeFocusCallback: Function = () => null
  private invokeBlurCallback: Function = () => null
  private invokeKeyDownCallback: Function = () => null
  private flatpickrInstance?: FlatpickrInstance
  private inputEl?: HTMLInputElement | HTMLTextAreaElement | HTMLDivElement
  private altInputEl?: HTMLInputElement
  private componentDidLoadComplete = false
  private valueDidChange = false
  private resizeTimer?: ReturnType<typeof setTimeout>
  private inputDatepickerEl?: HTMLSdxInputDatepickerElement
  private dateInputFlyoutEl?: HTMLSdxMenuFlyoutElement
  private infoTipFlyoutContentEl?: HTMLDivElement
  private infoTipFlyoutToggleEl?: HTMLSdxMenuFlyoutToggleElement
  private inputElValue = ""

  @Element() public el!: HTMLSdxInputElement

  @State() private hasInputElFocus: boolean = false

  /**
   * * Description text read by the screen reader. Will be appended to the loading spinner when `loading="true"`.
   */
  @Prop() public srHint: string = ""

  /**
   * Text for the screen reader labelling the search button.
   */
  @Prop() public srHintSearchButton: string = ""

  /**
   * Callback that will fire on hitting enter.
   * @deprecated use sdxsearch event instead.
   */
  @Prop() public hitEnterCallback?: (() => void) | string

  /**
   * Callback that will fire when the value has changed, regardless of method
   * (keyboard or programmatical).
   * @deprecated use input event instead.
   */
  @Prop() public changeCallback?: ((value: string) => void) | string

  /**
   * Callback that will fire when the value changes using the keyboard.
   * @deprecated use input event instead.
   */
  @Prop() public inputCallback?: ((value: string) => void) | string

  /**
   * Callback that will fire when the input gets focus.
   * @deprecated use focus event instead.
   */
  @Prop() public focusCallback?: (() => void) | string

  /**
   * Callback that will fire when the input loses focus.
   * @deprecated use blur event instead.
   */
  @Prop() public blurCallback?: (() => void) | string

  /**
   * Callback that will fire when a key is pressed down.
   * @deprecated use keydown event instead.
   */
  @Prop() public keyDownCallback?: ((e: CustomEvent) => void) | string

  /**
   * Default text that will be shown unless there’s a value.
   */
  @Prop() public placeholder: string = ""

  /**
   * Default input field type (e.g. "search", "password", "date", etc.). When
   * set to "date", flatpickr will take over. Caution: flatpickr does not
   * fulfill the accessibility requirements of SDX, yet (but we’re on it…).
   */
  @Prop() public type:
    | "text"
    | "password"
    | "search"
    | "textarea"
    | "email"
    | "date"
    | "tel"
    | "url"
    | "number" = "text"

  /**
   * Can be used for both reading and writing the value.
   */
  @Prop({ mutable: true }) public value: string = ""

  /**
   * Marks the text within the input on focus.
   */
  @Prop() public selectTextOnFocus: boolean = false

  /**
   * Disabled, but focussable using tab.
   */
  @Prop() public readonly: boolean = false

  /**
   * Disabled and not focussable using tab.
   */
  @Prop({ reflect: true }) public disabled: boolean = false

  /**
   * Text that will be written on the top.
   */
  @Prop({ reflect: true }) public label?: string

  /**
   * Allowed number of characters. Will show a counter for valid types.
   * [Not valid for all types](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#maxlength).
   */
  @Prop() public maxlength?: number

  /**
   * Sets the minimum number of lines that are shown inside the textarea.
   * Similar to HTML [`rows`](https://developer.mozilla.org/en-US/docs/Web/API/HTMLTextAreaElement/rows) attribute.
   */
  @Prop() public minRows?: number

  /**
   * Datepicker settings.
   * When `type="date"`, flatpickr is used and can be configured via this
   * attribute. Examples:
   * `{ locale: "fr" }`, `{ minDate: "today" }` or
   * `{ "altInput": true, "altFormat": "l j. F, Y" }`.
   * See [Picker (date)](components_-_picker_(date).html#overview) for
   * available options.
   */
  @Prop() public flatpickrOptions: any

  /**
   * Name parameter (useful when the component is used in a traditional HTML
   * form submit).
   */
  @Prop() public name?: string

  /**
   * Set this to false to declare the component as invalid and use the
   * `validation-message` attribute to explain why.
   */
  @Prop() public valid?: boolean

  /**
   * Text that explains the validation status to the user.
   */
  @Prop() public validationMessage?: string

  /**
   * Marks the component as required (please note that this itself does not
   * handle validation &mdash; use the "valid" and "validation-message" for
   * that).
   */
  @Prop() public required: boolean = false

  /**
   * Focuses the input field as soon as it’s rendered.
   */
  @Prop() public autofocus: boolean = false

  /**
   * Which keyboard to be displayed on touchscreens.
   */
  @Prop() public inputmode?: "decimal"

  /**
   * Additional information for screen readers when infoTip element is focused.
   */
  @Prop() public infoTipSrHint?: string = `${translate(
    "information for",
    this.el.lang,
  )} ${this.label}`

  /**
   * Specify what, if any, permission the user agent has to provide automated
   * assistance in filling out form field values, as well as guidance to the
   * browser as to the type of information expected in the field.
   * See [mdn web docs](//developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete).
   *
   * Please do not confuse it with `keyboard-behavior="autocomplete"`.
   */
  @Prop() public autocomplete?: string

  /**
   * Renders an icon button for clearing the input value.
   * Not yet public because maybe it will become the default behavior, but
   * it's really hard to know which input fields are clearable and which ones
   * are not.
   * @private
   */
  @Prop() public clearable: boolean = false

  /**
   * Renders a div that looks exactly like an input field. Overflowing content
   * will have an ellipsis. Used for sdx-select.
   * Only implemented for `type="text"`.
   * @private
   */
  @Prop() public editable: boolean = true

  /**
   * @private
   */
  @Prop() public inputStyle: Record<string, string | undefined> = {}

  /**
   * Display a badge right to the text. Just for sdx-select.
   * @private
   */
  @Prop() public badge?: string

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  /**
   * Hide the counter. Needed in sdx-input-mask and sdx-select.
   * @private
   */
  @Prop() public hideCounter: boolean = false

  /**
   * Shows a loading spinner and disables the `sdx-input`. Useful for async
   * data fetching. It also makes sure that the `noMatchesFoundLabel` won't
   * show. Don’t forget to set srHint accordingly.
   * It's best practice to wait about 1s before showing. For loading times
   * longer than 10s consider a skeleton instead.
   */
  @Prop() public loading: boolean = false

  /**
   * Hide the validation indicator. Needed in sdx-numeric-stepper.
   * @private
   */
  @Prop() public hideValidationIndicator: boolean = false

  @Watch("value")
  public valueChanged() {
    if (!this.componentDidLoadComplete) {
      return
    }

    this.valueDidChange = true

    if (
      this.flatpickrInstance &&
      this.inputElValue &&
      this.inputElValue !== this.value
    ) {
      // assume that flatpickr has autocorrected the value
      dispatchEvent(this.el, "input")
    }

    // Transfer value into flatpickr
    this.flatpickrInstance?.setDate(this.value)

    // Transfer value into altInput
    if (this.altInputEl) {
      this.altInputEl.value = this.getAltInputValue()
    }

    this.invokeChangeCallback(this.value)
  }

  @Watch("hitEnterCallback")
  public hitEnterCallbackChanged() {
    this.setInvokeHitEnterCallback()
  }

  @Watch("changeCallback")
  public changeCallbackChanged() {
    this.setInvokeChangeCallback()
  }

  @Watch("inputCallback")
  public inputCallbackChanged() {
    this.setInvokeInputCallback()
  }

  @Watch("focusCallback")
  public focusCallbackChanged() {
    this.setInvokeFocusCallback()
  }

  @Watch("blurCallback")
  public blurCallbackChanged() {
    this.setInvokeBlurCallback()
  }

  @Watch("keyDownCallback")
  public keyDownCallbackChanged() {
    this.setInvokeKeyDownCallback()
  }

  @Watch("flatpickrOptions")
  public flatpickrOptionsChanged() {
    if (!this.componentDidLoadComplete) {
      return
    }

    this.updateFlatpickrInstance()
  }

  /**
   * This component supports common native HTML form input element events (such
   * as `input`, `focus`, `blur`, etc.).
   */
  @Event() public common!: EventEmitter

  /**
   * Emitted when users press the enter key or clicks on search button when
   * `type="search"` is set.
   */
  @Event() public sdxsearch!: EventEmitter

  // This is a workaround needed because of a possible bug in ngModel:
  // It might be this bug: https://github.com/angular/angular/issues/13792
  // Without the workaround, this happens (testable in sdx-angular-example-deprecated):
  // End user types "asdf":
  // - changeCallback("asdf") --> correct
  // - [changeCallback]="onChange" executes onChange("asdf") --> correct
  // - ngModel="name" --> "asd" incorrect, the last letter is missing?
  @Listen("focus", { capture: true })
  public onFocus() {
    forceUpdate(this.el)
  }

  @Listen("keydown")
  public onKeyDown(e: KeyboardEvent) {
    this.onInputElKeyDown(e)
  }

  @Listen("click")
  public onClick() {
    this.openDatePicker(true)
  }

  @Listen("resize", { target: "window" })
  public onWindowResize() {
    if (this.type !== "date" || !this.getParsedFlatpickrOptions().weekNumbers) {
      return
    }

    clearTimeout(this.resizeTimer)

    this.resizeTimer = setTimeout(() => {
      const isOpen = this.flatpickrInstance?.isOpen
      // Workaround to fix calendar width
      this.updateFlatpickrInstance()

      if (isOpen) {
        this.openDatePicker()
      }
    }, 100)
  }

  /**
   * Set focus to element.
   */
  @Method()
  public async doFocus() {
    this.inputEl?.focus()
  }

  @Method()
  public async doBlur() {
    this.inputEl?.blur()
  }

  constructor() {
    this.setInvokeHitEnterCallback()
    this.setInvokeChangeCallback()
    this.setInvokeInputCallback()
    this.setInvokeFocusCallback()
    this.setInvokeBlurCallback()
    this.setInvokeKeyDownCallback()
  }

  public componentDidLoad() {
    // Move children to flyoutContent because slot loses assignedElements when moved in DOM.
    // No need to append elements on open or close, see SDX-1744.
    this.infoTipFlyoutContentEl?.append(
      ...this.el.querySelectorAll("[slot=infotip]"),
    )

    this.updateFlatpickrInstance()
    this.updateHeight()

    if (this.autofocus) {
      // timeout because of https://github.com/ionic-team/stencil/issues/3246
      setTimeout(() => {
        this.doFocus()
      })
    }

    this.componentDidLoadComplete = true
  }

  public componentDidRender() {
    this.updateInputElValueIfOutOfSync()
  }

  public componentDidUpdate() {
    if (this.valueDidChange) {
      this.updateHeight()
      this.valueDidChange = false
    }
  }

  private onInputElKeyPress(e: Event) {
    const keyboardEvent: KeyboardEvent = e as KeyboardEvent

    if (keyboardEvent.key === "Enter") {
      this.invokeHitEnterCallback()
      this.sdxsearch.emit()
    }
  }

  private getAnimationDuration(): number {
    if (!this.componentDidLoadComplete) {
      return 0
    }

    return this.animated ? this.animationDuration : 0
  }

  private onInputElKeyDown(e: Event) {
    const keyboardEvent: KeyboardEvent = e as KeyboardEvent

    if (keyboardEvent.key === "Tab") {
      this.dateInputFlyoutEl?.close()

      if (this.flatpickrInstance) {
        this.value = this.flatpickrInstance.input.value
      }
    }

    if (this.readonly) {
      return
    }

    if (keyboardEvent.key === "Enter" || keyboardEvent.key === "ArrowDown") {
      this.openDatePicker()
    }

    this.invokeKeyDownCallback(e)
  }

  private focusDatePicker() {
    // focus to enable keyboard navigation
    const selected = this.flatpickrInstance?.calendarContainer.querySelector(
      ".flatpickr-day.selected",
    ) as HTMLSpanElement

    const today = this.flatpickrInstance?.calendarContainer.querySelector(
      ".flatpickr-day.today",
    ) as HTMLSpanElement

    const firstFocusableEl =
      this.flatpickrInstance?.calendarContainer.querySelector(
        ".flatpickr-day:not(.flatpickr-disabled)",
      ) as HTMLSpanElement

    if (selected) {
      selected.focus()
    } else if (today) {
      today.focus()
    } else if (firstFocusableEl) {
      firstFocusableEl.focus()
    }
  }

  private async openDatePicker(withoutFocus?: boolean) {
    if (!this.flatpickrInstance || this.readonly) {
      return
    }

    if (this.flatpickrInstance.weekNumbers) {
      // Workaround to fix calendar width, see SDX-1670
      this.updateFlatpickrInstance()
    }

    await this.dateInputFlyoutEl?.open()
    !withoutFocus && this.focusDatePicker()
  }

  private onInputElInput(): void {
    if (!this.isInputElement(this.inputEl)) {
      return
    }

    this.inputElValue = this.inputEl.value

    // Do not use e.target because Firefox retargets event if there is a parent <button disabled>.
    // Happens when used in sdx-select (autocomplete).
    this.invokeInputCallback(this.inputEl.value)
  }

  // Used when "inputCallback" prop is provided
  private customInputCallback(value: string) {
    parseFunction(this.inputCallback)(value)

    // After invoking the above callback, "this.value" is now updated.
    // If it holds another value than what the user has typed, overwrite
    // that users input with "this.value".
    // However, this is only possible *after* render, therefore a rerender is triggered.
    if (this.value !== value) {
      forceUpdate(this.el)
    }
  }

  // Used when *no* "inputCallback" prop is provided
  private defaultInputCallback(value: string): void {
    this.value = value
  }

  private setInvokeHitEnterCallback(): void {
    this.invokeHitEnterCallback = parseFunction(this.hitEnterCallback)
  }

  private setInvokeChangeCallback(): void {
    this.invokeChangeCallback = parseFunction(this.changeCallback)
  }

  private setInvokeInputCallback(): void {
    if (this.inputCallback) {
      this.invokeInputCallback = this.customInputCallback
    } else {
      this.invokeInputCallback = this.defaultInputCallback
    }
  }

  private setInvokeFocusCallback(): void {
    this.invokeFocusCallback = parseFunction(this.focusCallback)
  }

  private setInvokeBlurCallback(): void {
    this.invokeBlurCallback = parseFunction(this.blurCallback)
  }

  private setInvokeKeyDownCallback(): void {
    this.invokeKeyDownCallback = parseFunction(this.keyDownCallback)
  }

  private onInputElFocus(): void {
    this.hasInputElFocus = true

    if (this.selectTextOnFocus) {
      this.selectText()
    }

    this.invokeFocusCallback()
  }

  private onInputElBlur(): void {
    this.hasInputElFocus = false
    this.invokeBlurCallback()
  }

  private selectText(): void {
    setTimeout(() => {
      if (!this.isInputElement(this.inputEl)) {
        return
      }

      this.inputEl.setSelectionRange(0, this.inputEl.value.length)
    })
  }

  /**
   * Calculate and set the autocomplete height.
   * If it has content, it should shrink or grow based on the content (within
   * a certain range).
   */
  private updateHeight(): void {
    // Only change the height of textarea
    if (this.type !== "textarea") {
      return
    }

    const inputEl = this.inputEl!

    const paddingVertical =
      getNumericValue("padding-top", inputEl) +
      getNumericValue("padding-bottom", inputEl)

    const border =
      getNumericValue("border-top-width", inputEl) +
      getNumericValue("border-bottom-width", inputEl)

    const lineHeight = getNumericValue("line-height", inputEl)
    const contentLines = getNumberOfLines(this.value)
    const emptyHeight = paddingVertical + border + lineHeight
    const contentHeight = emptyHeight + lineHeight * contentLines
    const maxHeight = emptyHeight + lineHeight * 10

    const minHeight = this.minRows ? emptyHeight + lineHeight * this.minRows : 0
    const constrainedHeight = Math.max(contentHeight, minHeight)
    const nextHeight = Math.min(constrainedHeight, maxHeight)

    animation.start(
      inputEl,
      {
        height: `${nextHeight}px`,
      },
      this.getAnimationDuration(),
    )
  }

  // Make sure the inputEl never has its "own" state but reflects "this.value".
  private updateInputElValueIfOutOfSync() {
    if (
      this.isInputElement(this.inputEl) &&
      this.value !== this.inputEl.value
    ) {
      // Just update the value if something has changed to avoid setting "" in
      // case inputEl has an invalid value.
      this.inputEl.value = this.value || ""
    }
  }

  private isInputElement(
    el?: HTMLElement,
  ): el is HTMLInputElement | HTMLTextAreaElement {
    return !isNil(el) && "value" in el
  }

  private getAltInputValue() {
    return this.flatpickrInstance?.altInput?.value || ""
  }

  private createAltInput() {
    if (this.flatpickrInstance?.altInput && !this.altInputEl) {
      this.altInputEl = document.createElement("input")

      this.altInputEl.value = this.getAltInputValue()
      this.altInputEl.setAttribute("class", `${this.inputEl!.className} alt`)
      this.altInputEl.style.position = "absolute"
      this.altInputEl.style.top = "0"
      this.altInputEl.style.width = "100%"
      this.altInputEl.style.height = "100%"

      this.altInputEl.oninput = (e) => {
        const target = e.target as HTMLInputElement
        // don't let flatpickr create weird date
        const newDate = new Date(target.value)
        this.flatpickrInstance?.setDate(newDate)
      }

      this.inputEl?.parentElement!.append(this.altInputEl)
    }
  }

  private async updateFlatpickrInstance() {
    if (this.inputDatepickerEl) {
      const flatpickrOptions: FlatpickrOptions = {
        locale: "de",
        dateFormat: "d.m.Y", // e.g. "15.01.2017"
        time_24hr: true,
        monthSelectorType: "static",
        ...this.getParsedFlatpickrOptions(),
        // these attributes should not be overwritten by consumer:
        onChange: () => {
          // wait till flatpickr updated the input value
          setTimeout(() => dispatchEvent(this.el, "input"))
          this.dateInputFlyoutEl?.close()
        },
        defaultDate: this.value,
        inline: true,
      }

      this.flatpickrInstance =
        await this.inputDatepickerEl.updateFlatpickrInstance(flatpickrOptions)

      // calendarContainer gets focused when pressing Tab key
      this.flatpickrInstance.calendarContainer.onfocus = () =>
        this.focusDatePicker()

      this.createAltInput()

      // copy some important attributes to altInput field
      if (this.altInputEl) {
        for (let prop of Object.entries(this.getAltInputProps())) {
          const [propName, propValue] = prop

          if (propValue !== undefined) {
            this.altInputEl.setAttribute(propName, propValue.toString())
          }
        }
      }
    }
  }

  private clear(): void {
    this.value = ""
    dispatchEvent(this.el, "input")
  }

  private isClearable(): boolean {
    return this.clearable && this.editable
  }

  private hasClearIcon(): boolean {
    return this.isClearable() && !!this.value
  }

  private createCounter() {
    const validTypes: Array<Input["type"]> = [
      "textarea",
      "text",
      "search",
      "url",
      "tel",
      "email",
      "password",
    ]
    const { value, maxlength, hideCounter } = this
    const valueLength = value ? value.length : 0

    const shouldShowCounter =
      !hideCounter && maxlength && validTypes.includes(this.type)

    if (!shouldShowCounter) {
      return
    }

    return (
      <Fragment>
        {/* label-wrapper class needed for font and color styles */}
        <span aria-hidden="true" class="label-wrapper counter">
          {valueLength}/{maxlength}
        </span>
        <span role="status" class="sr-only">
          {maxlength - valueLength} {translate("characters left", this.el.lang)}
          .
        </span>
      </Fragment>
    )
  }

  private createLabel() {
    const { label, required } = this
    const counterEl = this.createCounter()

    if (label || counterEl) {
      return (
        <div class="label-row">
          <div class="label-wrapper">
            <label htmlFor="id">
              {label}
              {required && <span aria-hidden="true">&nbsp;*</span>}
            </label>

            {hasSlot(this.infoTipFlyoutContentEl || this.el, "infotip") && (
              <sdx-menu-flyout
                animated={this.animated}
                displayChangeCallback={(display) => {
                  if (display === "closing") {
                    this.infoTipFlyoutToggleEl?.doFocus()
                  }
                }}
              >
                <sdx-menu-flyout-toggle
                  srHint={this.infoTipSrHint}
                  ref={(el) => (this.infoTipFlyoutToggleEl = el)}
                >
                  <sdx-icon icon-name="icon-information-circle" />
                </sdx-menu-flyout-toggle>

                <sdx-menu-flyout-content>
                  <div
                    id="sdx-dialog-content-description"
                    ref={(el) => (this.infoTipFlyoutContentEl = el)}
                  />
                </sdx-menu-flyout-content>
              </sdx-menu-flyout>
            )}
          </div>

          {counterEl}
        </div>
      )
    }
  }

  private getInputStyle(): Record<string, string | undefined> {
    if (this.hasClearIcon()) {
      const paddingRight = parseFloat(this.inputStyle.paddingRight!) || 0

      return {
        ...this.inputStyle,
        paddingRight: `${paddingRight + 40}px`, // $baseline-5
      }
    }

    return this.inputStyle
  }

  // needed for altInput feature
  private getAltInputProps() {
    return {
      autocomplete: this.autocomplete,
      autoCorrect: "off",
      autoCapitalize: "off",
      spellcheck: false,
      size: 1, // don't make sdx-input's width depending on its font-size
      placeholder: this.placeholder,
      "aria-invalid": `${this.valid === false}`,
      "aria-label": this.srHint,
      "aria-describedby": "description",
    }
  }

  private getInputProps() {
    return {
      id: "id",
      name: this.name, // used to enable autofill on Chrome and Edge
      class: "input",
      maxLength: this.maxlength,
      style: this.getInputStyle(),
      required: this.required,
      disabled: this.disabled || this.loading,
      readonly: this.readonly,
      ...this.getAltInputProps(),
      onInput: () => this.onInputElInput(),
      onKeyPress: (e: Event) => this.onInputElKeyPress(e),
      onFocus: () => this.onInputElFocus(),
      onBlur: () => this.onInputElBlur(),
      ref: (el?: typeof this.inputEl) => (this.inputEl = el),
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [this.type]: true,
      [getAppearance(this.el)]: true,
      readonly: this.readonly,
      disabled: this.disabled,
      editable: this.editable,
      clearable: this.clearable,
      valid: this.valid === true,
      invalid: this.valid === false,
    }
  }

  private onSearchButtonClick() {
    this.sdxsearch.emit()
  }

  private createSearchField() {
    return (
      <Fragment>
        <input
          {...this.getInputProps()}
          type={this.type}
          inputmode={this.inputmode}
        />

        <div class="inline-buttons" aria-live="polite">
          {!this.loading && (
            <div class="clear-button">
              <sdx-animation
                animationName={!!this.value ? "scale-in" : "scale-out"}
                animationDuration={this.getAnimationDuration()}
              >
                <sdx-button
                  theme="transparent"
                  onClick={() => this.clear()}
                  iconName="icon-clear-circle"
                  srHint={translate("Clear input")}
                  disabled={this.disabled || this.readonly}
                />
              </sdx-animation>
            </div>
          )}

          {this.loading ? (
            <sdx-loading-spinner
              class="loading-spinner"
              srHint={this.srHint ? this.srHint : undefined}
              strokeInherit
            />
          ) : (
            <sdx-button
              class="search-button"
              theme="transparent"
              onClick={() => this.onSearchButtonClick()}
              srHint={this.srHintSearchButton}
              iconName="icon-search"
              iconSize={3}
              disabled={this.disabled || this.readonly}
            />
          )}
        </div>
      </Fragment>
    )
  }

  private createInputField() {
    return (
      <Fragment>
        {this.editable && (
          <input
            {...this.getInputProps()}
            type={this.type}
            inputmode={this.inputmode}
          />
        )}

        {this.isClearable() && (
          <div class="inline-buttons">
            <div class="clear-button">
              <sdx-animation
                animationName={this.hasClearIcon() ? "scale-in" : "scale-out"}
                animationDuration={this.getAnimationDuration()}
              >
                <sdx-button
                  theme="transparent"
                  onClick={() => this.clear()}
                  iconName="icon-clear-circle"
                  aria-hidden="true"
                  tabindex={-1}
                  disabled={this.disabled || this.readonly}
                />
              </sdx-animation>
            </div>
          </div>
        )}

        {!this.editable && (
          <div
            {
              ...this.getInputProps() /* needed for aria attrs */
            }
            tabIndex={this.disabled ? undefined : 0}
          >
            {this.value && <sdx-text-truncate>{this.value}</sdx-text-truncate>}

            {!this.value && this.placeholder && (
              <sdx-text-truncate class="placeholder">
                {this.placeholder}
              </sdx-text-truncate>
            )}

            {/* Reserve space for badge to ensure a calm layout */}
            {typeof this.badge === "string" && (
              <span class="badge">{this.badge}</span>
            )}
          </div>
        )}

        {this.editable && this.valid && !this.hideValidationIndicator && (
          <sdx-icon
            class="checkmark-icon"
            iconName="icon-check-mark"
            size={3}
          />
        )}
      </Fragment>
    )
  }

  private createDateInputField() {
    return (
      <Fragment>
        <sdx-menu-flyout
          ref={(el) => (this.dateInputFlyoutEl = el)}
          animated={this.animated}
          displayChangeCallback={(display) => {
            // sync value here to preserve cursor position
            if (display === "closing") {
              this.value = this.flatpickrInstance!.input.value
            }
          }}
        >
          <sdx-menu-flyout-content
            autofocus={false}
            trapFocusFirstFocusableElement={
              this.flatpickrInstance?.calendarContainer
            }
            bodyStyle={{ padding: "0px" }}
            arrowStyles={{
              positionBottomLightTheme: {
                backgroundColor: "#eef3f6" /* $color-horizon */,
              },
              positionTopDarkTheme: {
                backgroundColor: "#1d1d1d" /* $color-dusk-tint-2-dark-theme */,
              },
            }}
          >
            <sdx-input-datepicker ref={(el) => (this.inputDatepickerEl = el)} />
          </sdx-menu-flyout-content>
        </sdx-menu-flyout>

        <input {...this.getInputProps()} />

        <sdx-button
          tabindex="-1"
          class="calendar-button"
          theme="transparent"
          iconName="icon-calendar"
          iconSize={3}
          disabled={this.disabled || this.readonly}
          valid={!(this.valid === false && !this.hasInputElFocus)}
          onClick={(event) => {
            event.stopPropagation()
            this.openDatePicker(true)
          }}
        />
      </Fragment>
    )
  }

  private createTextArea() {
    return <textarea {...this.getInputProps()} class="input textarea" />
  }

  /**
   * Parses and returns the flatpickrOptions prop.
   */
  private getParsedFlatpickrOptions(): FlatpickrOptions {
    let flatpickrOptions: FlatpickrOptions = {}

    if (this.flatpickrOptions) {
      if (typeof this.flatpickrOptions === "string") {
        flatpickrOptions = JSON.parse(this.flatpickrOptions)
      } else {
        // assume it's a valid flatpickr options object
        flatpickrOptions = this.flatpickrOptions
      }
    }

    return flatpickrOptions
  }

  public render() {
    renderHiddenInput(this.el, this.name, this.value, this.disabled)

    return (
      <div class={this.getComponentClassNames()}>
        {this.createLabel()}

        <div class="wrapper" key="wrapper">
          {(() => {
            switch (this.type) {
              case "textarea":
                return this.createTextArea()

              case "date":
                return this.createDateInputField()

              case "search":
                return this.createSearchField()

              default:
                return this.createInputField()
            }
          })()}
        </div>

        <div id="description">
          {this.validationMessage && (
            <Fragment>
              {/* extra span needed because validation-message is hidden on focus and therefore not read by screen reader */}
              <span class="sr-only">{this.validationMessage}</span>

              <sdx-validation-message
                validationMessage={this.validationMessage}
                aria-hidden="true"
              />
            </Fragment>
          )}
        </div>
      </div>
    )
  }
}
